import { useNavigate } from "react-router-dom";
import { Layers, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/Reveal";
import { SectionLabel } from "@/components/common";
import AgentRecommender from "@/components/marketing/AgentRecommender";
import { AI_IMAGES } from "@/lib/aiImages";
import { useLanguage } from "@/context/LanguageContext";

const cardImages = [AI_IMAGES.support, AI_IMAGES.recruitment, AI_IMAGES.sales, AI_IMAGES.research, AI_IMAGES.operations, AI_IMAGES.personal];

export default function Solutions() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const solutions = t("sol.cards").map((c, i) => ({ ...c, img: cardImages[i] }));
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden vora-hero-radial border-b border-white/8">
        <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8 py-24 lg:py-28">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3.5 py-1.5">
              <Layers className="h-3.5 w-3.5 text-[#4F8CFF]" />
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#8fb4ff]">{t("sol.label")}</span>
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08] max-w-3xl">
              {t("sol.h1a")} <span className="vora-text-gradient">{t("sol.accent")}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed" data-testid="solutions-hero-intro">
              {t("sol.intro")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Industry agents */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel>{t("sol.where")}</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
              {t("sol.h2")}
            </h2>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {solutions.map((s, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div className="h-full vora-card vora-card-hover rounded-2xl overflow-hidden group" data-testid={`solution-card-${i + 1}`}>
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      data-testid={`solution-card-img-${i + 1}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1424] via-transparent to-transparent" aria-hidden />
                  </div>
                  <div className="p-6 pt-4">
                    <h3 className="text-lg font-semibold text-white">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                    <div className="mt-4 space-y-2">
                      {s.tasks.map((task) => (
                        <div key={task} className="flex items-center gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="h-4 w-4 text-[#4F8CFF] shrink-0" /> {task}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 vora-card rounded-2xl p-8 sm:p-10 text-center">
              <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("sol.ctaH")}</h3>
              <p className="mt-3 text-base text-slate-300 max-w-xl mx-auto leading-relaxed">
                {t("sol.ctaP")}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/contact")} className="h-12 px-7 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold group" data-testid="solutions-cta-contact">
                  {t("sol.ctaBtn")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button onClick={() => navigate("/ai")} variant="outline" className="h-12 px-7 bg-transparent border-white/15 text-white hover:bg-white/5 rounded-full" data-testid="solutions-cta-ai">
                  {t("sol.ctaAi")}
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <AgentRecommender />
    </div>
  );
}
